import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, Search, RefreshCw, Phone, User, HelpCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { createActivityEntry } from "@/lib/deliveryLogFormat";
import { appendNoteAtomic } from "@/lib/atomicNoteAppend";
import { useToast } from "@/hooks/use-toast";
import { ORDER_SOURCE, ORDER_STATUS } from "@/lib/constants";
import type { ParsedOrderData, ConversationType } from "@/types/order";
import { useLanguage } from "@/contexts/LanguageContext";
import { OrderReviewSummary } from "./OrderReviewSummary";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
interface SmartOrderInputProps {
  onOrderCreated: () => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  onLoadingChange?: (loading: boolean) => void;
  /** 
   * Callback to expose primary action to parent (for footer button)
   * Called whenever the step state changes with current action details
   */
  onPrimaryActionChange?: (action: {
    label: string;
    onClick: () => void;
    disabled: boolean;
  }) => void;
}

// Non-order detection state
interface NonOrderState {
  conversationType: ConversationType;
  customerName?: string;
  customerPhone?: string;
  orderReference?: string;
  messageKey: string;
  suggestionKey: string;
}
export const SmartOrderInput = ({
  onOrderCreated,
  onCancel,
  onDirtyChange,
  onLoadingChange,
  onPrimaryActionChange
}: SmartOrderInputProps) => {
  const [pastedText, setPastedText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [parsedData, setParsedData] = useState<ParsedOrderData | null>(null);
  const [nonOrderState, setNonOrderState] = useState<NonOrderState | null>(null);
  const [loading, setLoading] = useState(false);
  const {
    toast
  } = useToast();
  const {
    language,
    t
  } = useLanguage();

  // Notify parent of dirty state based on actual content
  const isDirty = pastedText.trim().length > 0 || parsedData !== null;
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Notify parent of loading state for overlay action bar
  useEffect(() => {
    onLoadingChange?.(loading || isProcessing);
  }, [loading, isProcessing, onLoadingChange]);

  // Expose current primary action to parent (for footer button)
  // Child owns step state, parent just uses the exposed handler/label
  useEffect(() => {
    if (!onPrimaryActionChange) return;
    if (parsedData) {
      // Post-parse: primary is "Save"
      onPrimaryActionChange({
        label: t("common.save"),
        onClick: handleSave,
        disabled: loading || !parsedData.customer_name || !parsedData.category
      });
    } else {
      // Pre-parse: primary is "Add Order" (point #9)
      onPrimaryActionChange({
        label: isProcessing ? t("orders.buttons.processingAI") : t("orders.buttons.addOrder"),
        onClick: handleProcessText,
        disabled: isProcessing || !pastedText.trim()
      });
    }
  }, [parsedData, loading, isProcessing, pastedText, t, onPrimaryActionChange]);

  // Update dirty state when content changes
  const handleTextChange = (value: string) => {
    setPastedText(value);
  };
  const handleProcessText = async () => {
    if (!pastedText.trim()) {
      toast({
        title: t("common.error"),
        description: t("orders.errors.pasteTextRequired"),
        variant: "destructive"
      });
      return;
    }
    setIsProcessing(true);
    setNonOrderState(null);
    setParsedData(null);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('process-order-input', {
        body: {
          input_text: pastedText
        }
      });
      if (error) throw error;

      // Handle different response statuses
      if (data?.status === 'not_an_order') {
        // Non-order conversation detected
        setNonOrderState({
          conversationType: data.conversation_type,
          customerName: data.extracted_reference?.customer_name,
          customerPhone: data.extracted_reference?.customer_phone,
          orderReference: data.extracted_reference?.order_reference,
          messageKey: data.message_key || data.conversation_type,
          suggestionKey: data.suggestion_key || data.conversation_type
        });
        toast({
          title: t("orders.notAnOrder.title"),
          description: t("orders.notAnOrder.description"),
          variant: "default"
        });
      } else if (data?.status === 'incomplete_order') {
        // Incomplete order - show what we have with warning
        setParsedData(data.data);
        toast({
          title: t("orders.incomplete.title"),
          description: data.message || t("orders.incomplete.description"),
          variant: "default"
        });
      } else if (data?.status === 'success' && data?.data) {
        setParsedData(data.data);
        toast({
          title: t("orders.success.extractionSuccess"),
          description: t("orders.success.orderExtracted")
        });
      } else {
        throw new Error(t("orders.errors.failedToExtract"));
      }
    } catch (error: any) {
      console.error('Processing error:', error);
      toast({
        title: "Parsing Failed",
        description: error.message || t("orders.errors.extractionFailed"),
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };
  const handleStartOver = () => {
    setParsedData(null);
    setNonOrderState(null);
    setPastedText("");
  };
  const getConversationTypeLabel = (type: ConversationType): string => {
    const labels: Record<ConversationType, string> = {
      'new_order': t("orders.conversationType.newOrder"),
      'order_modification': t("orders.conversationType.modification"),
      'order_inquiry': t("orders.conversationType.inquiry"),
      'complaint': t("orders.conversationType.complaint"),
      'general_inquiry': t("orders.conversationType.general"),
      'unclear': t("orders.conversationType.unclear")
    };
    return labels[type] || type;
  };
  const handleSave = async () => {
    if (!parsedData?.customer_name) {
      toast({
        title: t("common.error"),
        description: t("orders.errors.customerNameRequired"),
        variant: "destructive"
      });
      return;
    }
    if (!parsedData?.category) {
      toast({
        title: t("common.error"),
        description: t("orders.errors.categoryRequired"),
        variant: "destructive"
      });
      return;
    }

    // Check for duplicate order number
    if (parsedData.order_number) {
      const {
        data: existingOrders
      } = await supabase.from("orders").select("id").eq("order_number", parsedData.order_number).limit(1);
      if (existingOrders && existingOrders.length > 0) {
        toast({
          title: t("orders.duplicate.title"),
          description: t("orders.duplicate.description").replace("{{orderNumber}}", parsedData.order_number),
          variant: "destructive"
        });
        return;
      }
    }
    setLoading(true);
    try {
      // Get current user
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Step 1: Find or create customer (handled by DB trigger auto_link_customer_on_order)
      // The trigger will automatically find or create the customer record
      // No need for manual customer lookup — avoids duplicates from mismatched logic

      // Step 2: Create order with customer_id
      const {
        error
      } = await supabase.from("orders").insert({
        order_number: parsedData.order_number || null,
        customer_name: parsedData.customer_name,
        customer_phone: parsedData.customer_phone || null,
        address: parsedData.address || null,
        message_raw: pastedText,
        parsed_items: parsedData.products ? JSON.stringify(parsedData.products) : null,
        total_price: parsedData.total_price || null,
        comments: parsedData.comments || null,
        source: parsedData.source_channel || ORDER_SOURCE.MANUAL,
        category: parsedData.category,
        order_status: ORDER_STATUS.NEW,
        csr_name: parsedData.csr_name || null,
        user_id: user.id,
        requested_delivery_date: parsedData.requested_delivery_date || null
      });
      if (error) throw error;

      // Write initial activity log — fetch inserted order ID
      try {
        const { data: insertedOrders } = await supabase
          .from("orders")
          .select("id")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1);
        if (insertedOrders?.[0]?.id) {
          const logEntry = createActivityEntry("system", "order_received", undefined, language as "en" | "ka");
          await appendNoteAtomic(insertedOrders[0].id, logEntry, false);
        }
      } catch (logErr) {
        console.warn("[SmartInput] Failed to write activity log:", logErr);
      }

      toast({
        title: t("orders.success.extractionSuccess"),
        description: t("orders.success.orderCreated")
      });
      onOrderCreated();
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // --- PRE-PARSE STATE ---
  if (!parsedData) {
    // Show non-order feedback if detected
    if (nonOrderState) {
      return <Card className="border-2 border-warning/50 bg-warning/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-warning">
              <AlertTriangle className="h-5 w-5" />
              {t("orders.notAnOrder.title")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{t("orders.notAnOrder.detected")}:</span>{" "}
                {getConversationTypeLabel(nonOrderState.conversationType)}
              </p>
              <p className="text-sm">
                {t(`orders.notAnOrder.messages.${nonOrderState.messageKey}`) || t("orders.notAnOrder.messages.default")}
              </p>
            </div>

            {/* Extracted reference data */}
            {(nonOrderState.customerName || nonOrderState.customerPhone || nonOrderState.orderReference) && <div className="p-3 rounded-lg bg-muted/50 space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t("orders.notAnOrder.extractedInfo")}
                </p>
                {nonOrderState.customerName && <div className="flex items-center gap-2 text-sm">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>{nonOrderState.customerName}</span>
                  </div>}
                {nonOrderState.customerPhone && <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span className="font-mono">{nonOrderState.customerPhone}</span>
                  </div>}
                {nonOrderState.orderReference && <div className="flex items-center gap-2 text-sm">
                    <Search className="h-4 w-4 text-muted-foreground" />
                    <span>{t("orders.notAnOrder.orderRef")}: {nonOrderState.orderReference}</span>
                  </div>}
              </div>}

            {/* Suggestion */}
            <div className="p-3 rounded-lg bg-primary/5 border border-primary/20">
              <p className="text-sm">
                <span className="font-medium">{t("orders.notAnOrder.suggestion")}:</span>{" "}
                {t(`orders.notAnOrder.suggestions.${nonOrderState.suggestionKey}`) || t("orders.notAnOrder.suggestions.default")}
                {nonOrderState.customerPhone && <span className="block mt-1 text-muted-foreground">
                    {t("orders.notAnOrder.suggestions.phoneHint").replace("{{phone}}", nonOrderState.customerPhone)}
                  </span>}
              </p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              {nonOrderState.customerPhone && <Button variant="outline" onClick={() => {
              // Copy phone to clipboard for easy search
              navigator.clipboard.writeText(nonOrderState.customerPhone!);
              toast({
                title: t("orders.notAnOrder.phoneCopied"),
                description: t("orders.notAnOrder.phoneCopiedDesc")
              });
            }} className="flex-1">
                  <Phone className="h-4 w-4 mr-2" />
                  {t("orders.notAnOrder.copyPhone")}
                </Button>}
              <Button variant="outline" onClick={handleStartOver} className="flex-1">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t("orders.notAnOrder.tryDifferent")}
              </Button>
            </div>
          </CardContent>
        </Card>;
    }

    // Workbench layout: dominant paste surface, minimal noise
    return <div className="flex h-full flex-col gap-3">
        {/* Label + help icon row */}
        <div className="flex items-center gap-1.5">
          <Label htmlFor="pastedText">{t("orders.labels.pasteOrderMessage")}</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <button type="button" className="text-muted-foreground hover:text-foreground transition-colors">
                <HelpCircle className="h-3.5 w-3.5" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">
                {t("orders.example.hint") || "Paste messages from WhatsApp, Viber, or any messenger. Include customer name, phone, address, and products."}
              </p>
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Primary work surface - dominant paste area */}
        <div className="flex-1 min-h-[220px]">
          <Textarea id="pastedText" placeholder={t("orders.placeholders.pasteMessage") || "Paste order message here"} value={pastedText} onChange={e => handleTextChange(e.target.value)} className="h-full resize-none font-mono text-sm border-2 bg-muted/20 rounded-xl focus-within:border-primary/50" />
        </div>

        {/* Single muted helper line */}
        
      </div>;
  }

  // --- POST-PARSE REVIEW STATE ---
  // Form wrapper allows overlay footer to trigger submission
  return <form onSubmit={e => {
    e.preventDefault();
    handleSave();
  }}>
      <OrderReviewSummary data={parsedData} onDataChange={setParsedData} onStartOver={handleStartOver} />
      {/* LAW: Forms never render global actions - overlay footer owns Save/Cancel */}
    </form>;
};